import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import {
  itemKey,
  maxForPack,
  useCart,
  type PackCartItem,
  type PackSnapshot,
} from '@/features/cart/CartContext';
import { useI18n } from '@/client/i18n/I18nContext';
import { PackCollage } from '@/components/PackCollage';
import { PACK_PATH, packName } from '@/lib/catalog/packs';
import { formatMoney } from '@/lib/money';
import { lp } from '@/client/i18n/locale';

/** «Incluye: 3× A, 1× B (Talla M)» for a pack line, in the active language. */
export function usePackIncludes(): (pack: PackSnapshot) => string {
  const { t } = useI18n();
  return (pack) =>
    `${t('pack.includes')} ${pack.components
      .map((c) => `${c.quantity}× ${c.name}${c.size ? ` (${t('cart.size')} ${c.size})` : ''}`)
      .join(', ')}`;
}

/**
 * A pack line in the cart drawer / cart page (same `.cart-line` grid as a
 * product line): photo or collage, name, what it includes, +/- capped by the
 * line's cap, price × quantity (the API's pack price), remove.
 */
export function CartPackLine({
  line,
  onNavigate,
  style,
}: {
  line: PackCartItem;
  /** Called when a link is followed (the drawer closes itself). */
  onNavigate?: () => void;
  style?: CSSProperties;
}) {
  const { t, locale } = useI18n();
  const { setQty, remove } = useCart();
  const includes = usePackIncludes();
  const { pack } = line;
  const name = packName(pack, locale);
  const key = itemKey(line);

  return (
    <div className="cart-line" style={style}>
      <Link
        to={lp(`${PACK_PATH}/${pack.slug}`)}
        className={`cart-line__media${pack.image ? '' : ' cart-line__media--pack'}`}
        onClick={onNavigate}
      >
        {pack.image ? (
          <img src={pack.image} alt={name} />
        ) : (
          <PackCollage items={pack.components} alt={name} sizes="80px" />
        )}
      </Link>
      <div>
        <span className="card__pack">{t('pack.badge').replace('{pct}', String(pack.discountPercent))}</span>
        <p style={{ fontFamily: 'var(--font-serif)' }}>{name}</p>
        <span className="cart-line__includes">{includes(pack)}</span>
        <div className="qty" style={{ marginTop: 'var(--space-2)' }}>
          <button type="button" aria-label={t('a11y.qtyDecrease')} onClick={() => setQty(key, line.quantity - 1)}>
            −
          </button>
          <span>{line.quantity}</span>
          <button
            type="button"
            aria-label={t('a11y.qtyIncrease')}
            disabled={line.quantity >= maxForPack(pack)}
            onClick={() => setQty(key, line.quantity + 1)}
          >
            +
          </button>
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div className="price">{formatMoney(pack.priceCents * line.quantity, pack.currency)}</div>
        <button
          type="button"
          className="link-underline text-muted"
          style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--space-2)' }}
          onClick={() => remove(key)}
        >
          {t('cart.remove')}
        </button>
      </div>
    </div>
  );
}
